import {
  Alert,
  Backdrop,
  Box,
  Button,
  CircularProgress,
  Divider,
  Drawer,
  FormControl,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Popover,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { Apps, useAppContext } from '../contexts/AppContext';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Server } from '../services/Server';
import { useNavigate, useParams } from 'react-router-dom';
import SaveIcon from '@mui/icons-material/Save';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { generatePages } from './Pages';
import AppSoilingAssessment from '../components/app-assessment/AppSoilingAssessment';
import toast from 'react-hot-toast';
import { handleError } from '../utils/Error';
import AppSoilingMitigationStrategy from '../components/app-optimization/AppSoilingMitigationStrategy.tsx';
import { compare_array_dates, getYearlyDaysFromDate } from '../utils/utils';
import ChartHelpTooltip from '../components/common/ChartHelpTooltip';
import TextField from '../components/inputs/TextField';
import NumberField from '../components/inputs/NumberField';
import ArrayField from '../components/inputs/ArrayField';
import SpecialArrayField from '../components/inputs/SpecialArrayField';
import CheckBoxField from '../components/inputs/CheckboxField';
import DateField from '../components/inputs/DateField';
import SelectField from '../components/inputs/SelectField';
import SpecialMultiSelect from '../components/inputs/SpecialMultiSelect';
import SoilingRatioMultiSelect from '../components/inputs/SoilingRatioMultiSelect';
import Map from '../components/common/Map';
import SwitchField from '../components/inputs/SwitchField';
import CallContent from '../components/inputs/CallContentField';
import UploadField from '../components/inputs/UploadField';
import { useFormContext } from '../contexts/FormContext';
import MonthlyValuesField from '../components/inputs/MonthlyValuesField';
import ButtonGroupField from '../components/inputs/ButtonGroupField';
import AppSoilingOperationalStrategy from '../components/app-optimization/AppSoilingOperationalStrategy';
import CloseIcon from '@mui/icons-material/Close';

const Project = () => {
  const queryClient = useQueryClient();
  const [, updateState] = useState({});
  const forceUpdate = useCallback(() => updateState({}), []);
  const { role, completed, setCompleted, activeStep, setActiveStep, referenceProjects, setApp, modified, setModified } = useAppContext();
  const { formData, setFormData, getFormDataItem, updateDataValue, results, setResults, handleChange, clearSoilingRatioSelections } =
    useFormContext();
  const { getProjectAPI, getCleaningPackagesAPI, postProjectAPI, putProjectAPI, postSensorDataAPI } = Server();
  const [sectionName, setSectionName] = useState('location');
  const [pages, setPages] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingSensorData, setIsLoadingSensorData] = useState(false);
  const [preservedPatternData, setPreservedPatternData] = useState<{ [key: string]: any }>({});
  const lastCompletedStateRef = useRef<string>('');
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);

  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  const navigate = useNavigate();

  const mapRef = useRef<any>(null);
  const params = useParams();

  const checkCompleted = (requiredSteps: number[]) => {
    let disabled = false;
    requiredSteps?.forEach((requiredStep) => {
      if (!completed.hasOwnProperty(requiredStep) || !completed[requiredStep]) {
        disabled = true;
      }
    });
    return disabled;
  };

  const findRequiredFields = (fields: any[]): { name: string; label: string; default?: any }[] => {
    const requiredFields: { name: string; label: string; default?: any }[] = [];

    const processField = (field: any) => {
      let isHidden = field.hasOwnProperty('isHidden') ? getFormDataItem(formData, field?.isHidden) : false;

      // Special handling for complex isHidden conditions
      if (field?.isHidden === 'yield_evaluation.source') {
        const source = getFormDataItem(formData, 'yield_evaluation.source');
        isHidden = source !== 'monthly';
      }

      if (field.hasOwnProperty('revertHidden') && field?.revertHidden) {
        isHidden = !isHidden;
      }

      let isDisabled = field.hasOwnProperty('disabled') ? getFormDataItem(formData, field?.disabled) : false;

      if (field.required && field.name && !isHidden && !isDisabled) {
        // Special handling for dynamic soiling sensor data blocks: expand required names per existing block
        if (field.name === 'soiling_sensor_data.name' || field.name === 'soiling_sensor_data.type') {
          const blockKeys = Object.keys(formData).filter((key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'));
          if (blockKeys.length > 0) {
            blockKeys.forEach((bk) =>
              requiredFields.push({
                name: `${bk}.${field.name.split('.').pop()}`,
                label: field.label,
                default: field.default,
              }),
            );
          }
          // If there are no blocks, do not push the base name; zero blocks should not block navigation
        } else {
          requiredFields.push({ name: field.name, label: field.label, default: field.default });
        }
      }

      if (field.fields && !isHidden && !isDisabled) {
        field.fields.forEach(processField);
      }
    };

    fields.forEach(processField);
    return requiredFields;
  };

  const isValidForm = () => {
    const currentPage = pages[activeStep];
    if (!currentPage) {
      return true;
    }

    const requiredFields = findRequiredFields(currentPage.fields);

    for (const field of requiredFields) {
      let value = getFormDataItem(formData, field.name);

      // Apply default value if field has one and value is empty
      if ((value === null || value === undefined || value === '') && field.default) {
        value = field.default;
      }

      if (
        value === null ||
        value === undefined ||
        value === '' ||
        (Array.isArray(value) && !value.every((item) => item !== null && item !== ''))
      ) {
        toast.error(`Please fill in the ${field.label} field before proceeding`);
        return false;
      }
    }
    return true;
  };

  const { data: cleaningPackages = {}, isSuccess: isCleaningPackagesLoaded } = useQuery({
    queryKey: ['cleaning_packages'],
    queryFn: () => getCleaningPackagesAPI(),
    enabled: role === 'mitigation_strategy' || role === 'assessment' || role === 'operational_strategy',
  });

  const { data: project = undefined } = useQuery({
    queryKey: ['project-' + params?.id],
    queryFn: () => getProjectAPI(params?.id as string),
    enabled: !!params?.id,
  });

  useEffect(() => {
    setApp(Apps.SoilingMitigation);
    setActiveStep(0);
  }, []);

  useEffect(() => {
    setSectionName('location');
  }, [role]);

  useEffect(() => {
    setPages(generatePages(referenceProjects, cleaningPackages, params?.id).filter((step) => step.roles.includes(role)));
  }, [role, isCleaningPackagesLoaded, params?.id]);

  useEffect(() => {
    if (validateFields(pages[activeStep])) {
      // Create a stable string representation of completed state to avoid unnecessary updates
      const completedStateKey = `${activeStep}-${completed[activeStep]}`;
      if (lastCompletedStateRef.current !== completedStateKey) {
        handleComplete();
        forceUpdate();
        lastCompletedStateRef.current = completedStateKey;
      }

      if (
        mapRef.current &&
        formData?.location?.latitude &&
        formData?.location?.longitude &&
        !isNaN(Number(formData.location.latitude)) &&
        !isNaN(Number(formData.location.longitude))
      ) {
        mapRef.current.setView([Number(formData.location.latitude), Number(formData.location.longitude)]);
      }
    }
  }, [formData, activeStep, pages]);

  useEffect(() => {
    const defaultFormData = {};
    // Only process default values if pages are loaded
    if (pages && pages.length > 0) {
      pages.forEach((page) => {
        if (page.default_values) {
          Object.keys(page.default_values).forEach((key) => {
            // Only set default value if the field doesn't already exist in formData
            const existingValue = getFormDataItem(formData, key);
            if (existingValue === undefined || existingValue === null || existingValue === '') {
              updateDataValue(defaultFormData, key, page.default_values[key]);
            }
          });
        }
      });
    }

    if (!project && isCleaningPackagesLoaded && cleaningPackages && Object.keys(cleaningPackages).length > 0) {
      // Only add cleaning_packages if it doesn't already exist
      if (!formData.cleaning_packages) {
        updateDataValue(defaultFormData, 'cleaning_packages', cleaningPackages);
      }
    }

    // Always merge with existing formData to preserve user input
    if (project || Object.keys(defaultFormData).length > 0) {
      const mergedData = {
        ...formData,
        ...defaultFormData,
        ...project,
      };
      // Transform soiling sensor data from array to object structure
      const transformedData = transformSoilingSensorDataFromArray(mergedData);

      // Preserve existing pattern data (chart data) when merging
      const finalData = { ...transformedData };

      // Find all soiling sensor data blocks in the transformed data
      const transformedSoilingKeys = Object.keys(transformedData).filter(
        (key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'),
      );

      // For each transformed block, preserve its pattern data from preserved data
      transformedSoilingKeys.forEach((transformedKey) => {
        // Try to find matching pattern data from preserved data
        let patternToRestore = null;

        // First, try exact key match
        if (preservedPatternData[transformedKey]) {
          patternToRestore = preservedPatternData[transformedKey];
        } else {
          // If no exact match, try to find by file_uid
          const transformedFileUid = getFormDataItem(transformedData, `${transformedKey}.file_uid`);
          if (transformedFileUid) {
            Object.keys(preservedPatternData).forEach((preservedKey) => {
              const preservedFileUid = getFormDataItem(formData, `${preservedKey}.file_uid`);
              if (preservedFileUid === transformedFileUid) {
                patternToRestore = preservedPatternData[preservedKey];
              }
            });
          }
        }

        if (patternToRestore) {
          updateDataValue(finalData, `${transformedKey}.pattern`, patternToRestore);
        }
      });

      setFormData(finalData);
    } else if (!formData || Object.keys(formData).length === 0) {
      setFormData({});
    }

    if (modified) {
      setModified(false);
    }
    setResults(project?.results);

    document.title = 'Project | ' + (project?.name ? project?.name : 'Assessment');
  }, [project, pages, cleaningPackages, isCleaningPackagesLoaded]);

  // Function to transform soiling sensor data array back to object structure for UI
  const transformSoilingSensorDataFromArray = (data: any) => {
    if (!data || !data.soiling_sensor_data || !Array.isArray(data.soiling_sensor_data)) {
      return data;
    }

    const transformedData = { ...data };
    const soilingSensorArray = transformedData.soiling_sensor_data;

    // Remove the array
    delete transformedData.soiling_sensor_data;

    // Purge any existing soiling sensor data block keys to avoid duplicates
    Object.keys(transformedData)
      .filter((key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'))
      .forEach((key) => {
        delete (transformedData as any)[key];
      });

    // Transform array back to object structure
    soilingSensorArray.forEach((item: any, index: number) => {
      const blockKey = index === 0 ? 'soiling_sensor_data' : `soiling_sensor_data_${index + 1}`;
      (transformedData as any)[blockKey] = {
        file_uid: item.file_uid,
        _order: item._order || index + 1,
        name: item.name || '', // Include the name field
        type: item.type || 'rde300', // Include the type field
      };
    });

    return transformedData;
  };

  // Refresh soiling sensor data when user navigates to sensor data tab
  useEffect(() => {
    if (sectionName !== 'soiling_sensor' || !formData || !formData.location?.latitude || !formData.location?.longitude) {
      return;
    }

    // Find all soiling sensor data blocks
    const soilingDataKeys = Object.keys(formData).filter((key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'));

    // Check if there are any blocks with file_uid to load
    const blocksToLoad = soilingDataKeys.filter((blockKey) => {
      const fileUid = getFormDataItem(formData, `${blockKey}.file_uid`);
      return !!fileUid;
    });

    if (blocksToLoad.length === 0) {
      return; // No data to load
    }

    // Start loading
    setIsLoadingSensorData(true);

    // Create promises for all sensor data requests
    const sensorDataPromises = blocksToLoad.map((blockKey) => {
      const fileUid = getFormDataItem(formData, `${blockKey}.file_uid`);
      const sensorType = getFormDataItem(formData, `${blockKey}.type`) || 'rde300';

      return postSensorDataAPI(
        {
          file_uid: fileUid,
          file_type: sensorType,
          project_id: params.id || '',
          latitude: formData.location.latitude,
          longitude: formData.location.longitude,
        },
        {
          plant: getFormDataItem(formData, 'plant'),
          use_plant_geometry: getFormDataItem(formData, 'use_plant_geometry'),
        },
      )
        .then((sensorData) => {
          // Update the pattern field with the fresh data
          const patternFieldName = `${blockKey}.pattern`;
          setFormData((prevFormData: any) => {
            const newFormData = { ...prevFormData };
            updateDataValue(newFormData, patternFieldName, sensorData);
            return newFormData;
          });
          return { blockKey, success: true };
        })
        .catch((error) => {
          console.error(`Error refreshing sensor data for block ${blockKey}:`, error);

          // Clear the pattern data when there's an error to show empty graph instead of old data
          const patternFieldName = `${blockKey}.pattern`;
          setFormData((prevFormData: any) => {
            const newFormData = { ...prevFormData };
            updateDataValue(newFormData, patternFieldName, null);
            return newFormData;
          });

          return { blockKey, success: false, error };
        });
    });

    // Wait for all requests to complete
    Promise.all(sensorDataPromises)
      .then((results) => {
        const successCount = results.filter((r) => r.success).length;
        const errorCount = results.filter((r) => !r.success).length;

        if (errorCount > 0) {
          toast.error(`Failed to load data for ${errorCount} sensor(s)`);
        } else if (successCount > 0) {
          toast.success(`Loaded data for ${successCount} sensor(s)`);
        }
      })
      .finally(() => {
        // Stop loading regardless of success/failure
        setIsLoadingSensorData(false);
      });
  }, [sectionName, formData?.location?.latitude, formData?.location?.longitude, params.id]);

  // Track previous sensor types to detect changes
  const [previousSensorTypes, setPreviousSensorTypes] = useState<{ [key: string]: string }>({});

  // Watch for sensor type changes and refetch sensor data
  useEffect(() => {
    if (!formData || !formData.location?.latitude || !formData.location?.longitude) {
      return;
    }

    // Find all soiling sensor data blocks
    const soilingDataKeys = Object.keys(formData).filter((key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'));

    // Get current sensor types
    const currentSensorTypes: { [key: string]: string } = {};
    soilingDataKeys.forEach((blockKey) => {
      const sensorType = getFormDataItem(formData, `${blockKey}.type`) || 'rde300';
      currentSensorTypes[blockKey] = sensorType;
    });

    // Find blocks where sensor type actually changed
    const blocksToRefetch = soilingDataKeys.filter((blockKey) => {
      const fileUid = getFormDataItem(formData, `${blockKey}.file_uid`);
      const currentType = currentSensorTypes[blockKey];
      const previousType = previousSensorTypes[blockKey];

      // Only refetch if we have a file_uid AND the sensor type actually changed
      return !!fileUid && previousType && currentType !== previousType;
    });

    // Update the previous sensor types for next comparison
    setPreviousSensorTypes(currentSensorTypes);

    if (blocksToRefetch.length === 0) {
      return; // No data to refetch
    }

    // Start loading
    setIsLoadingSensorData(true);

    // Create promises for sensor data requests only for blocks with changed types
    const sensorDataPromises = blocksToRefetch.map((blockKey) => {
      const fileUid = getFormDataItem(formData, `${blockKey}.file_uid`);
      const sensorType = getFormDataItem(formData, `${blockKey}.type`) || 'rde300';

      return postSensorDataAPI(
        {
          file_uid: fileUid,
          file_type: sensorType,
          project_id: params.id || '',
          latitude: formData.location.latitude,
          longitude: formData.location.longitude,
        },
        {
          plant: getFormDataItem(formData, 'plant'),
          use_plant_geometry: getFormDataItem(formData, 'use_plant_geometry'),
        },
      )
        .then((sensorData) => {
          // Update the pattern field with the fresh data
          const patternFieldName = `${blockKey}.pattern`;

          setFormData((prevFormData: any) => {
            const newFormData = { ...prevFormData };
            updateDataValue(newFormData, patternFieldName, sensorData);

            // Use the context function instead of local one
            clearSoilingRatioSelections('soiling_ratio_operational', fileUid);
            clearSoilingRatioSelections('soiling_ratio_operational_ex_post', fileUid);

            return newFormData;
          });
          return { blockKey, success: true };
        })
        .catch((error) => {
          console.error(`Error refetching sensor data for block ${blockKey}:`, error);

          // Clear the pattern data when there's an error to show empty graph instead of old data
          const patternFieldName = `${blockKey}.pattern`;
          setFormData((prevFormData: any) => {
            const newFormData = { ...prevFormData };
            updateDataValue(newFormData, patternFieldName, null);
            return newFormData;
          });

          return { blockKey, success: false, error };
        });
    });

    // Wait for all requests to complete
    Promise.all(sensorDataPromises)
      .then((results) => {
        const successCount = results.filter((r) => r.success).length;
        const errorCount = results.filter((r) => !r.success).length;

        if (errorCount > 0) {
          toast.error(`Failed to refetch data for ${errorCount} sensor(s)`);
        } else if (successCount > 0) {
          toast.success(`Refetched data for ${successCount} sensor(s)`);
        }
      })
      .finally(() => {
        // Stop loading regardless of success/failure
        setIsLoadingSensorData(false);
      });
  }, [
    // Watch for changes in sensor types by creating a stable dependency
    JSON.stringify(
      Object.keys(formData || {})
        .filter((key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'))
        .reduce(
          (acc, key) => {
            acc[key] = getFormDataItem(formData, `${key}.type`) || 'rde300';
            return acc;
          },
          {} as { [key: string]: string },
        ),
    ),
    formData?.location?.latitude,
    formData?.location?.longitude,
    params.id,
  ]);

  const validateFields = (form: any) => {
    let valid = true;
    form?.fields?.forEach((field: any) => {
      if (['text', 'number'].includes(field.type) && !field.fields && !getFormDataItem(formData, field.name)) {
        valid = false;
      }
      if (field.hasOwnProperty('fields')) {
        valid = valid && validateFields(field);
      }
    });
    return form !== undefined && valid;
  };

  const handleComplete = () => {
    const newCompleted = { ...completed };
    newCompleted[activeStep] = true;
    setCompleted(newCompleted);
  };

  const cleanUserPatterns = (data: any) => {
    if (data.rain.from_api && data.rain?.rain_pattern) {
      delete data.rain.rain_pattern;
    }
    if (data.relative_humidity.from_api && data.relative_humidity?.pattern) {
      delete data.relative_humidity.pattern;
    }
    if (data.wind_speed.from_api && data.wind_speed?.pattern) {
      delete data.wind_speed.pattern;
    }
    if (data.soiling_ratio.from_api && data.soiling_ratio?.pattern) {
      delete data.soiling_ratio.pattern;
    }
    return data;
  };

  const createMutation = useMutation({
    mutationFn: (data: object) => {
      const cleanedData: any = cleanUserPatterns(data);
      setIsSaving(true);
      return params?.id
        ? putProjectAPI(params.id, { ...cleanedData, results: { ...results } })
        : postProjectAPI({ ...cleanedData, results: { ...results } });
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['soiling-assessment-projects'] });
      if (params?.id) {
        queryClient.invalidateQueries({ queryKey: ['project-' + params?.id] });
        // Don't reload sensor data after save - let the useEffect handle it
      } else if (data) {
        navigate('/project/' + data);
      }
      toast.success('Success!');
      setIsSaving(false);
    },
    onError: (error) => {
      handleError(error);
      setIsSaving(false);
    },
  });

  // Function to clean formData before saving (exclude pattern fields from soiling sensor data blocks)
  const cleanFormDataForSave = (data: any) => {
    const cleanedData = { ...data };

    // Find all soiling sensor data blocks
    const soilingDataKeys = Object.keys(cleanedData).filter(
      (key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'),
    );

    // Transform soiling sensor data blocks into an array
    const soilingSensorDataArray: any[] = [];

    soilingDataKeys.forEach((blockKey) => {
      if (cleanedData[blockKey] && typeof cleanedData[blockKey] === 'object') {
        const blockData = cleanedData[blockKey];
        if (blockData.file_uid) {
          soilingSensorDataArray.push({
            file_uid: blockData.file_uid,
            type: blockData.type || 'rde300',
            _order: blockData._order || 0,
            name: blockData.name || '', // Include the name field
          });
        }
        // Remove the individual block from cleanedData
        delete cleanedData[blockKey];
      }
    });

    // Add the array to cleanedData
    if (soilingSensorDataArray.length > 0) {
      cleanedData.soiling_sensor_data = soilingSensorDataArray;
    }

    return cleanedData;
  };

  const handleSave = () => {
    if (isValidForm()) {
      // Preserve pattern data before saving
      const patternData: { [key: string]: any } = {};
      Object.keys(formData).forEach((key) => {
        if (key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_')) {
          const pattern = getFormDataItem(formData, `${key}.pattern`);
          if (pattern) {
            patternData[key] = pattern;
          }
        }
      });
      setPreservedPatternData(patternData);

      const cleanedFormData = cleanFormDataForSave(formData);
      createMutation.mutate(cleanedFormData);
    }
  };

  const handleDuplicate = async () => {
    try {
      setIsSaving(true);
      const { id, name, ...data } = project;
      const duplicatePayload = { ...data, name: `${name} - Copy ${new Date().toLocaleString()}` };
      await postProjectAPI(duplicatePayload);
      toast(
        () => (
          <span>
            <b>Project successfully duplicated: </b>
            <a href={`/projects`} target="_self">
              View projects
            </a>
          </span>
        ),
        {
          duration: 4000,
        },
      );
    } catch (err) {
      console.log(err);
      toast.error(`There was an error while trying to duplicate this project: ${err}`);
    } finally {
      setIsSaving(false);
    }
  };

  const renderSoilingSensorDataSection = (field: any) => {
    let isHidden = field.hasOwnProperty('isHidden') ? getFormDataItem(formData, field?.isHidden) : false;

    if (field.hasOwnProperty('revertHidden') && field?.revertHidden) {
      isHidden = !isHidden;
    }

    if (isHidden) {
      return null;
    }

    // Get all soiling sensor data blocks
    let soilingDataKeys = Object.keys(formData).filter((key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'));

    return (
      <Grid size={12} key={field.label}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Box display="flex" alignItems="center">
            <Typography color="text.primary" variant={field.font_size || 'h6'}>
              {field.label}
            </Typography>
            {field.hasOwnProperty('help') ? <ChartHelpTooltip chartType={field.help} /> : null}
          </Box>
          <Button
            variant="contained"
            size="small"
            startIcon={<ContentCopyIcon />}
            disabled={isLoadingSensorData}
            onClick={() => {
              // Create a new soiling sensor data block with an order counter
              const existingBlocks = Object.keys(formData).filter(
                (key) => key === 'soiling_sensor_data' || key.startsWith('soiling_sensor_data_'),
              );
              const nextOrder = existingBlocks.length + 1;
              const newBlockName = `soiling_sensor_data_${nextOrder}`;

              // Create new block with simplified structure
              const newBlockData: any = {
                file_uid: null, // Will be set when file is uploaded
                name: `Sensor ${nextOrder}`, // Default name with increment
                _order: nextOrder, // Add order for sorting
                type: 'rde300',
              };

              // Update form data with proper deep copy
              const newFormData = JSON.parse(JSON.stringify(formData));
              updateDataValue(newFormData, newBlockName, newBlockData);
              setFormData(newFormData);

              // Mark as modified
              setModified(true);
            }}
          >
            Add New Block
          </Button>
        </Box>

        {/* Show loader instead of content when loading */}
        {isLoadingSensorData ? (
          <Backdrop open={true} sx={{ width: '100%', height: '450px', position: 'relative' }}>
            <CircularProgress />
          </Backdrop>
        ) : /* Render all existing blocks */
        soilingDataKeys.length > 0 ? (
          soilingDataKeys
            .sort((a, b) => {
              // Get order from formData for proper sorting
              const orderA = getFormDataItem(formData, `${a}._order`) || 0;
              const orderB = getFormDataItem(formData, `${b}._order`) || 0;
              return orderB - orderA; // Highest order first, so newest blocks always appear on top
            })
            .map((blockKey) => {
              return (
                <Grid
                  size={12}
                  key={blockKey}
                  sx={{
                    padding: 1,
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    borderRadius: '4px',
                    boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)',
                    mb: 2,
                  }}
                >
                  <Box display="flex" justifyContent="flex-end" alignItems="center" sx={{ mb: 1 }}>
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        // Get the file_uid of the block being removed
                        const fileUidToRemove = getFormDataItem(formData, `${blockKey}.file_uid`);

                        // Remove this block (including the first/only one)
                        const newFormData = { ...formData };
                        delete newFormData[blockKey];
                        setFormData(newFormData);

                        // Use the context function
                        if (fileUidToRemove) {
                          clearSoilingRatioSelections('soiling_ratio_operational', fileUidToRemove);
                          clearSoilingRatioSelections('soiling_ratio_operational_ex_post', fileUidToRemove);
                        }

                        setModified(true);
                      }}
                    >
                      Remove
                    </Button>
                  </Box>
                  <Grid container spacing={2}>
                    {field.fields.map((subField: any) => {
                      // Clone the subfield and update all references to use the block key
                      const clonedField = {
                        ...subField,
                        name: subField.name.replace('soiling_sensor_data', blockKey),
                        // Update upload configuration if it exists
                        upload: subField.upload
                          ? {
                              ...subField.upload,
                              name: subField.upload.name
                                ? subField.upload.name.replace('soiling_sensor_data', blockKey)
                                : subField.upload.name,
                              target: subField.upload.target
                                ? subField.upload.target.replace('soiling_sensor_data', blockKey)
                                : subField.upload.target,
                            }
                          : subField.upload,
                      };
                      // Enforce required for sensor name
                      if (clonedField.name.endsWith('.name')) {
                        (clonedField as any).required = true;
                      }
                      return getFieldComponent(clonedField);
                    })}
                  </Grid>
                </Grid>
              );
            })
        ) : (
          // Show empty state when no blocks exist
          <Grid
            size={12}
            sx={{
              padding: 2,
              border: '1px dashed rgba(0, 0, 0, 0.12)',
              borderRadius: '4px',
              textAlign: 'center',
              color: 'text.secondary',
            }}
          >
            <Typography variant="body2">No soiling sensor data blocks yet. Click "Add New Block" to create one.</Typography>
          </Grid>
        )}
      </Grid>
    );
  };

  const renderGroupField = (field: any) => {
    let isHidden = field.hasOwnProperty('isHidden') ? getFormDataItem(formData, field?.isHidden) : false;

    // Special handling for complex isHidden conditions
    if (field?.isHidden === 'yield_evaluation.source') {
      const source = getFormDataItem(formData, 'yield_evaluation.source');
      isHidden = source !== 'monthly';
    }

    if (field.hasOwnProperty('revertHidden') && field?.revertHidden) {
      isHidden = !isHidden;
    }

    if (isHidden) {
      return null;
    }

    return (
      <Grid
        size={12}
        key={field.label}
        sx={{ padding: 1, border: '1px solid rgba(0, 0, 0, 0.12)', borderRadius: '4px', boxShadow: '2px 2px 4px rgba(0, 0, 0, 0.2)' }}
      >
        <Box display="flex" sx={{ mb: 1 }}>
          <Typography color="text.primary" variant={field.font_size || 'h6'}>
            {field.label}
          </Typography>
          {field.hasOwnProperty('help') ? <ChartHelpTooltip chartType={field.help} /> : null}
        </Box>
        <Grid container spacing={2}>
          {field.fields.map((subField: any, index: number) => (
            <React.Fragment key={subField.name || index}>{getFieldComponent(subField)}</React.Fragment>
          ))}
        </Grid>
      </Grid>
    );
  };

  const renderToggleGroupFieldWithSubForms = (field: any) => (
    <Grid size={field.size || 12} key={field.name}>
      <Typography color="text.primary" variant="h6" sx={{ mb: 1 }}>
        {field.label}
      </Typography>
      <Grid container spacing={2}>
        <Grid size={12} key={field.label}>
          <FormControl fullWidth>
            <ToggleButtonGroup
              value={formData[field.name] || null}
              onChange={(_, value) => handleChange({ target: { name: field.name, value } }, field)}
              exclusive
              color="primary"
              fullWidth
            >
              {field.options.map((option: any) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </FormControl>
        </Grid>
        {field.options.map((option: any) => {
          return formData[field.name] && formData[field.name] === option.value && option.fields ? (
            <Grid size={option.size || 12} key={option.value + '_form'}>
              <Grid container spacing={2}>
                {option.fields.map((field: any) => getFieldComponent(field))}
              </Grid>
            </Grid>
          ) : null;
        })}
      </Grid>
    </Grid>
  );

  const renderList = (field: any) => (
    <Grid size={field.size || 12} key={field.name} sx={{ mb: 2 }}>
      <Grid container spacing={2}>
        {field?.fields?.map((field: any, index: number) => getFieldComponent(field, index))}
        <Grid>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              onClick={() => {
                let val;
                let new_element: Record<string, any> = {};
                field.fields.forEach((f: any) => {
                  new_element[f.name] = formData[f.name];
                });
                val = [new_element];
                if (getFormDataItem(formData, field.name)) {
                  val = getFormDataItem(formData, field.name);
                  val.push(new_element);
                }
                return handleChange({ target: { name: field.name, value: val, type: field.type, checked: false } }, field);
              }}
              disabled={field.hasOwnProperty('disabled') ? getFormDataItem(formData, field?.disabled) : false}
            >
              Add
            </Button>
            <Button
              variant="contained"
              color="error"
              onClick={() => {
                handleChange({ target: { name: field.name, value: [], type: field.type, checked: false } }, field);
              }}
              disabled={field.hasOwnProperty('disabled') ? getFormDataItem(formData, field?.disabled) : false}
            >
              Clear
            </Button>
          </Stack>
        </Grid>
      </Grid>
      {getFormDataItem(formData, field.name)?.length ? (
        <Grid container spacing={2} sx={{ mt: 2 }}>
          <ul style={{ listStyle: 'none', padding: 0, width: '100%' }}>
            {getFormDataItem(formData, field.name)
              ?.sort(compare_array_dates)
              .map((item: any, index: number) => {
                return Object.keys(item).map((key) => {
                  let d = new Date(item[key]);
                  return (
                    <li
                      key={`${index}-${key}`}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '8px 0',
                        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                      }}
                    >
                      <Typography color="text.primary">
                        {key}:{' '}
                        {field?.fields?.[0]?.format?.includes('YYYY')
                          ? d.getFullYear() + '-' + ('0' + (d.getMonth() + 1)).slice(-2) + '-' + ('0' + d.getDate()).slice(-2)
                          : d.getDate() + '/' + ('0' + (d.getMonth() + 1)).slice(-2)}
                      </Typography>
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent any parent click handlers
                          let val = getFormDataItem(formData, field.name);
                          val.splice(index, 1);
                          return handleChange({ target: { name: field.name, value: val, type: field.type, checked: false } }, field);
                        }}
                        sx={{
                          color: 'error.main',
                          '&:hover': {
                            backgroundColor: 'error.light',
                            color: 'error.contrastText',
                          },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </li>
                  );
                });
              })}
          </ul>
        </Grid>
      ) : null}
    </Grid>
  );

  const getFieldComponent = (field: any, index?: number) => {
    if (field?.hidden) {
      return null;
    }

    // Check isHidden condition
    let isHidden = field.hasOwnProperty('isHidden') ? getFormDataItem(formData, field?.isHidden) : false;

    // Special handling for complex isHidden conditions
    if (field?.isHidden === 'yield_evaluation.source') {
      const source = getFormDataItem(formData, 'yield_evaluation.source');
      isHidden = source !== 'monthly';
    }

    if (field.hasOwnProperty('revertHidden') && field?.revertHidden) {
      isHidden = !isHidden;
    }

    if (isHidden) {
      return null;
    }

    if (field.hasOwnProperty('roles') && !field.roles.includes(role)) {
      return null;
    }

    const key = field.name || `field-${index}`;

    switch (field.type) {
      case 'text':
        return <TextField key={key} field={field} />;
      case 'number':
        return <NumberField key={key} field={field} />;
      case 'array':
      case 'sarray': // string array: TODO: make this better :-)
        // Array is a comma separated list of numerical values. It's automatically parsed as an array in the formData object when it's modified.
        return <ArrayField key={key} field={field} getYearlyDaysFromDate={getYearlyDaysFromDate} />;
      case 'special_array':
        return <SpecialArrayField key={key} field={field} />;
      case 'checkbox':
        return <CheckBoxField key={key} field={field} />;
      case 'date':
        return <DateField key={key} field={field} />;
      case 'select':
        return <SelectField key={key} field={field} />;
      case 'multiSelectSpecial':
        return <SpecialMultiSelect key={key} field={field} />;
      case 'soilingRatioMultiSelect':
        return <SoilingRatioMultiSelect key={key} field={field} />;
      case 'map':
        return <Map key={key} mapRef={mapRef} field={field} />;
      case 'switch':
        return <SwitchField key={key} field={field} />;
      case 'callContent':
        return <CallContent key={key} field={field} />;
      case 'upload':
        return <UploadField key={key} field={field} />;
      case 'monthlyValues':
        return <MonthlyValuesField key={key} field={field} />;
      case 'list':
        return renderList(field);
      case 'group':
        // Special handling for Soiling Sensor Data to allow multiple blocks
        if (field.name === 'soiling_sensor_data') {
          return renderSoilingSensorDataSection(field);
        }
        return renderGroupField(field);
      case 'toggleGroupForm':
        return renderToggleGroupFieldWithSubForms(field);
      case 'buttonGroup':
        return <ButtonGroupField key={key} field={field} />;
      default:
        return null;
    }
  };

  const drawerWidth = 240;

  return (
    <Box sx={{ margin: 0, height: '100vh' }}>
      <Toolbar />
      <Box sx={{ display: 'flex' }}>
        <Drawer
          sx={{
            width: drawerWidth,
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: drawerWidth,
              boxSizing: 'border-box',
            },
          }}
          variant="permanent"
          anchor="left"
        >
          <Toolbar />
          <Toolbar>
            <Grid container>
              <Grid sx={{ width: '100%', mt: 2 }}>
                <Typography
                  variant="h6"
                  noWrap
                  component="div"
                  aria-owns={open ? 'mouse-over-popover' : undefined}
                  aria-haspopup="true"
                  onMouseEnter={handlePopoverOpen}
                  onMouseLeave={handlePopoverClose}
                >
                  {formData?.name || 'Plant Name'}
                </Typography>
                {formData?.name && (
                  <Popover
                    id="mouse-over-popover"
                    sx={{ pointerEvents: 'none' }}
                    open={open}
                    anchorEl={anchorEl}
                    anchorOrigin={{
                      vertical: 'bottom',
                      horizontal: 'left',
                    }}
                    transformOrigin={{
                      vertical: 'top',
                      horizontal: 'left',
                    }}
                    onClose={handlePopoverClose}
                    disableRestoreFocus
                  >
                    <Typography sx={{ p: 1 }}>{formData?.name}</Typography>
                  </Popover>
                )}
              </Grid>
              <Grid>
                <IconButton aria-label="save" onClick={handleSave}>
                  {isSaving ? (
                    <CircularProgress sx={{ height: '1em !important', width: '1em !important' }} />
                  ) : (
                    <SaveIcon sx={{ color: modified ? 'red' : 'none' }} />
                  )}
                </IconButton>
                {project && (
                  <IconButton aria-label="duplicate" onClick={handleDuplicate}>
                    {isSaving ? (
                      <CircularProgress sx={{ height: '1em !important', width: '1em !important' }} />
                    ) : (
                      <ContentCopyIcon sx={{ color: modified ? 'red' : 'none' }} />
                    )}
                  </IconButton>
                )}
              </Grid>
            </Grid>
          </Toolbar>
          <Divider />
          <List>
            {pages?.map((page, index) => {
              if (page.roles.includes(role)) {
                return page.divider ? (
                  <Divider />
                ) : (
                  <ListItem key={page.name} disablePadding>
                    <ListItemButton
                      sx={{
                        borderLeft: sectionName === page.section ? '5px solid #007acd' : '5px solid rgba(0, 0, 0, 0)',
                      }}
                      selected={sectionName === page.section}
                      onClick={() => {
                        if (isValidForm()) {
                          setActiveStep(index);
                          setSectionName(page.section);
                          if (page.hasOwnProperty('onClick')) {
                            page.onClick();
                          }
                        }
                      }}
                      disabled={(page.hasOwnProperty('disabled') && page.disabled) || checkCompleted(page.prerequisites)}
                    >
                      <ListItemIcon>{page.icon}</ListItemIcon>
                      <ListItemText primary={page.name} />
                    </ListItemButton>
                  </ListItem>
                );
              }
            })}
            <>
              <Divider />
              {['assessment'].includes(role) ? (
                <ListItem key={'as'} disablePadding>
                  <ListItemButton
                    sx={{
                      borderLeft: sectionName === 'soiling_risk_assessment' ? '5px solid #007acd' : '',
                    }}
                    selected={sectionName === 'soiling_risk_assessment'}
                    onClick={() => {
                      setActiveStep(-1);
                      setSectionName('soiling_risk_assessment');
                    }}
                  >
                    <ListItemIcon>
                      <AssessmentIcon />
                    </ListItemIcon>
                    <ListItemText primary={'Soiling Assessment'} />
                  </ListItemButton>
                </ListItem>
              ) : null}
              {['mitigation_strategy'].includes(role) ? (
                <ListItem key={'cs'} disablePadding>
                  <ListItemButton
                    sx={{
                      borderLeft: sectionName === 'mitigation_strategy' ? '5px solid #007acd' : '',
                    }}
                    selected={sectionName === 'mitigation_strategy'}
                    onClick={() => {
                      setActiveStep(-1);
                      setSectionName('mitigation_strategy');
                    }}
                  >
                    <ListItemIcon>
                      <CompareArrowsIcon />
                    </ListItemIcon>
                    <ListItemText primary={'Mitigation Strategy'} />
                  </ListItemButton>
                </ListItem>
              ) : null}
              {['ex_post_analysis'].includes(role) ? (
                <ListItem key={'csx'} disablePadding>
                  <ListItemButton
                    sx={{
                      borderLeft: sectionName === 'ex_post_analysis' ? '5px solid #007acd' : '',
                    }}
                    selected={sectionName === 'ex_post_analysis'}
                    onClick={() => {
                      setActiveStep(-1);
                      setSectionName('ex_post_analysis');
                    }}
                  >
                    <ListItemIcon>
                      <CompareArrowsIcon />
                    </ListItemIcon>
                    <ListItemText primary={'Ex-Post Analysis'} />
                  </ListItemButton>
                </ListItem>
              ) : null}
              {['operational_strategy'].includes(role) ? (
                <ListItem key={'cs'} disablePadding>
                  <ListItemButton
                    sx={{
                      borderLeft: sectionName === 'operational_strategy' ? '5px solid #007acd' : '',
                    }}
                    selected={sectionName === 'operational_strategy'}
                    onClick={() => {
                      setActiveStep(-1);
                      setSectionName('operational_strategy');
                    }}
                  >
                    <ListItemIcon>
                      <CompareArrowsIcon />
                    </ListItemIcon>
                    <ListItemText primary={'Operational Strategy'} />
                  </ListItemButton>
                </ListItem>
              ) : null}
            </>
          </List>
        </Drawer>
        <Box
          component="main"
          sx={{
            flexGrow: 1,
            bgcolor: 'background.default',
            p: 3,
            width: { xs: `calc(100% - ${drawerWidth}px)` },
          }}
        >
          <Box component="form" sx={{ display: 'flex', flexDirection: 'column', p: 1 }} noValidate>
            {modified || isSaving ? (
              <Alert severity="warning" sx={{ mb: 2 }}>
                You have unsaved changes. Click{' '}
                <Link onClick={handleSave} sx={{ cursor: 'pointer' }}>
                  here
                </Link>{' '}
                to save your changes.
              </Alert>
            ) : null}
            <Grid container spacing={2}>
              {pages[activeStep]?.description ? (
                <Box sx={{ mt: 1, mb: 2 }}>
                  {typeof pages[activeStep]?.description === 'object' && pages[activeStep]?.description !== null
                    ? pages[activeStep]?.description[role] || pages[activeStep]?.description
                    : pages[activeStep]?.description}
                </Box>
              ) : null}
              {pages[activeStep]?.fields.map((field: any) => getFieldComponent(field))}
            </Grid>
          </Box>
          {sectionName === 'soiling_risk_assessment' ? (
            formData.location?.latitude &&
            formData.location?.longitude &&
            formData.surface_tilt != undefined &&
            formData.cleaning_efficiency ? (
              <>
                <Box sx={{ mt: 0 }}>
                  <AppSoilingAssessment name={''} setResults={setResults} />
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ mt: 0 }}>
                  <Typography color="text.primary">Missing required fields.</Typography>
                </Box>
              </>
            )
          ) : null}
          {sectionName === 'mitigation_strategy' ? (
            formData?.location?.latitude && formData?.location?.longitude ? (
              <>
                <Box sx={{ mt: 0 }}>
                  <AppSoilingMitigationStrategy setResults={setResults} />
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ mt: 0 }}>
                  <Typography color="text.primary">Please choose a location to see results.</Typography>
                </Box>
              </>
            )
          ) : null}
          {sectionName === 'operational_strategy' ? (
            formData?.location?.latitude && formData?.location?.longitude ? (
              <>
                <Box sx={{ mt: 0 }}>
                  <AppSoilingOperationalStrategy setResults={setResults} project_id={params?.id as string} />
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ mt: 0 }}>
                  <Typography color="text.primary">Please choose a location to see results.</Typography>
                </Box>
              </>
            )
          ) : null}
          {sectionName === 'ex_post_analysis' ? (
            formData?.location?.latitude && formData?.location?.longitude ? (
              <>
                <Box sx={{ mt: 0 }}>
                  <AppSoilingOperationalStrategy type={'ex-post'} setResults={setResults} project_id={params?.id as string} />
                </Box>
              </>
            ) : (
              <>
                <Box sx={{ mt: 0 }}>
                  <Typography color="text.primary">Please choose a location to see results.</Typography>
                </Box>
              </>
            )
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default Project;
